/**
 * Geteilter Kontingent-State (GET /api/quota), damit Kopf-Balken (BrandHeader)
 * und Detail-Karte (QuotaCard) sich EINE Abfrage teilen statt doppelt zu fetchen.
 *
 * Verhalten 1:1 zur Web-App (public/assets/app.js → aktualisiereTokenBalken):
 *  - nur eingeloggt laden,
 *  - bei Fehler/fehlendem Token bleibt der Balken versteckt (kein Crash),
 *  - Monatsfenster bevorzugt, sonst das letzte Fenster.
 */
import { signal, computed } from "@preact/signals";
import { endpoints } from "../api/endpoints";
import type { QuotaWindow } from "../api/types";
import { token } from "./session";

/** null = noch nicht geladen / nicht verfügbar. */
export const quotaWindows = signal<QuotaWindow[] | null>(null);
export const quotaError = signal(false);

let inFlight: Promise<void> | null = null;

/**
 * Lädt die Kontingent-Fenster (gecacht: nur ein Request gleichzeitig).
 * `force` erzwingt eine frische Abfrage (z. B. nach Wiederanzeige der Karte).
 */
export function loadQuota(force = false): Promise<void> {
  if (!token.value) {
    // Ausgeloggt: nichts laden, alten Stand verwerfen.
    quotaWindows.value = null;
    quotaError.value = false;
    return Promise.resolve();
  }
  if (!force && (inFlight || quotaWindows.value)) {
    return inFlight ?? Promise.resolve();
  }
  inFlight = endpoints
    .quota()
    .then((res) => {
      quotaWindows.value = res.windows ?? [];
      quotaError.value = false;
    })
    .catch(() => {
      // Kein veralteter Wert: Fenster verwerfen → Kopf-Balken blendet sich aus
      // (monthUsagePct = null) und ein späterer (nicht-erzwungener) Aufruf lädt
      // frisch nach (Selbstheilung). Die Karte zeigt ihre eigene Fehlermeldung.
      quotaWindows.value = null;
      quotaError.value = true;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * Monats-Verbrauch in Prozent (verbraucht/limit), wie der Web-Balken.
 * null, wenn kein brauchbares Fenster (kein Limit) vorliegt.
 */
export const monthUsagePct = computed<number | null>(() => {
  const ws = quotaWindows.value;
  if (!ws || !ws.length) return null;
  // Monatsfenster bevorzugen, sonst das letzte Fenster (wie im Web).
  const w =
    ws.find((x) => /monat|month/i.test(String(x.label || x.key || ""))) ||
    ws[ws.length - 1];
  const limit = Number(w?.limit) || 0;
  if (!w || !limit) return null;
  const used = Number(w.used) || 0;
  return Math.min(100, Math.max(0, (used / limit) * 100));
});
